import React, { useState } from 'react';
import {
  Text, Select, Button, FileInput, TextInput, Textarea, Stack, Alert,
  Divider, Group
} from '@mantine/core';
import { IconAlertCircle, IconPlus, IconFileCode } from '@tabler/icons-react';
import { saveTenantProfile } from '../api_scripts/agentStudioClient';

/**
 * Step 3 — tenant context.
 *
 * Dropdown of existing tenant profiles (from DDB) plus an inline form to
 * create a new one. A profile holds:
 *   - orgId               — the tenant's WxCC org UUID
 *   - displayName         — friendly name (e.g. "Acme Dev")
 *   - template            — exported "one of every node" tenant flow JSON
 *   - defaultConnectorId  — tenant HTTP connector for middleware calls
 *   - defaultQueueId      — tenant queue for terminal handoffs
 *   - middlewareHints     — free-text middleware notes
 *   - middlewareBaseUrl   — informational only; connector handles real URL
 */
function TenantContextStep({ profiles, loading, selectedOrgId, onSelect, onProfileSaved, getApiToken }) {
  const [creating, setCreating] = useState(false);
  const [draftOrgId, setDraftOrgId] = useState('');
  const [draftDisplayName, setDraftDisplayName] = useState('');
  const [draftTemplateFile, setDraftTemplateFile] = useState(null);
  const [draftTemplate, setDraftTemplate] = useState(null);
  const [draftConnectorId, setDraftConnectorId] = useState('');
  const [draftQueueId, setDraftQueueId] = useState('');
  const [draftMiddlewareHints, setDraftMiddlewareHints] = useState('');
  const [draftMiddlewareBaseUrl, setDraftMiddlewareBaseUrl] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleTemplate = async (file) => {
    setError(null);
    setDraftTemplateFile(file);
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      setDraftTemplate(parsed);
      // Pre-fill orgId if present
      if (parsed.orgId && !draftOrgId) setDraftOrgId(parsed.orgId);
    } catch (err) {
      setError('Template file is not valid JSON: ' + err.message);
    }
  };

  const handleSave = async () => {
    setError(null);
    if (!draftOrgId) return setError('orgId is required');
    if (!draftTemplate) return setError('Template JSON is required');
    setSaving(true);
    try {
      const profile = {
        orgId: draftOrgId,
        displayName: draftDisplayName || draftOrgId,
        template: draftTemplate,
        defaultConnectorId: draftConnectorId || null,
        defaultQueueId: draftQueueId || null,
        middlewareHints: draftMiddlewareHints,
        middlewareBaseUrl: draftMiddlewareBaseUrl
      };
      await saveTenantProfile(profile, getApiToken);
      onProfileSaved(profile);
      setCreating(false);
      setDraftOrgId('');
      setDraftDisplayName('');
      setDraftTemplateFile(null);
      setDraftTemplate(null);
      setDraftConnectorId('');
      setDraftQueueId('');
      setDraftMiddlewareHints('');
      setDraftMiddlewareBaseUrl('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const profileOptions = (profiles || []).map((p) => ({
    value: p.orgId,
    label: `${p.displayName || p.orgId}  (${p.orgId.slice(0, 8)}…)`
  }));

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Pick the tenant whose WxCC schema the generated flow should match. If your tenant isn't here yet, create a profile with the "one of every node" flow you exported from Studio.
      </Text>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          {error}
        </Alert>
      )}

      <Select
        label="Tenant profile"
        placeholder={loading ? 'Loading…' : 'Pick a tenant'}
        data={profileOptions}
        value={selectedOrgId || null}
        onChange={onSelect}
        searchable
        clearable
        disabled={loading}
      />

      <Group>
        <Button
          variant="subtle"
          color="cyan"
          leftSection={<IconPlus size={14} />}
          onClick={() => setCreating((v) => !v)}
        >
          {creating ? 'Cancel new profile' : 'Create new profile'}
        </Button>
      </Group>

      {creating && (
        <>
          <Divider label="New tenant profile" labelPosition="left" />
          <Group grow>
            <TextInput
              label="orgId (WxCC org UUID)"
              placeholder="9ae8284d-…"
              value={draftOrgId}
              onChange={(e) => setDraftOrgId(e.target.value)}
              required
            />
            <TextInput
              label="Display name"
              placeholder="Acme Dev"
              value={draftDisplayName}
              onChange={(e) => setDraftDisplayName(e.target.value)}
            />
          </Group>

          <FileInput
            label='"One of every node" template flow (.json)'
            description="Export a flow from Studio that contains one each of: Start, VAV2 (both connectors), Parse, Case, HTTPRequest (with connectorId), QueueContact (with real queue), PlayMessage, PlayMusic, BlindTransfer, DisconnectContact, SetVariable, Condition."
            placeholder="Choose .json"
            accept="application/json,.json"
            leftSection={<IconFileCode size={16} />}
            value={draftTemplateFile}
            onChange={handleTemplate}
            required
          />

          <Group grow>
            <TextInput
              label="Default connector ID (HTTP)"
              placeholder="16a083fb-…"
              description="Tenant connector for middleware HTTP calls"
              value={draftConnectorId}
              onChange={(e) => setDraftConnectorId(e.target.value)}
            />
            <TextInput
              label="Default queue ID"
              placeholder="9849f4b1-…"
              description="Used for terminal QueueContact handoffs"
              value={draftQueueId}
              onChange={(e) => setDraftQueueId(e.target.value)}
            />
          </Group>

          <TextInput
            label="Middleware base URL (informational)"
            placeholder="https://app-acme-middleware-tst.example.com"
            value={draftMiddlewareBaseUrl}
            onChange={(e) => setDraftMiddlewareBaseUrl(e.target.value)}
          />

          <Textarea
            label="Middleware hints"
            description="Describe what middleware endpoints exist (paths, methods, auth) so the flow's HTTPRequest activities map correctly."
            placeholder="POST /api/v1/patients/search → look up patient by name + DOB + phone. POST /api/v1/appointments/future → ..."
            minRows={4}
            value={draftMiddlewareHints}
            onChange={(e) => setDraftMiddlewareHints(e.target.value)}
          />

          <Group justify="flex-end">
            <Button color="cyan" loading={saving} onClick={handleSave}>
              Save profile
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );
}

export default TenantContextStep;
